// Pausa de SLA ("estou indisponível agora") — lógica pura, sem acesso a banco,
// para poder rodar em node:test igual a escalaDisponibilidade/slaRanking. Quem
// busca e grava é o componente (src/components/PausaSlaToggle.tsx), pelas RPCs
// fn_pausar_sla/fn_retomar_sla.
//
// Uma pausa é uma linha de ausencias_trabalho com origem='pausa' e recorte de
// horário — de propósito: assim ela já é descontada por estaDisponivel (rodízio
// de leads), por tempoUtilDecorrido (SLA útil e painel de leads parados) e pela
// fn_esta_disponivel do banco, sem nenhuma conta nova.
import { paraLocalBRT, horaParaMinutos, estaDisponivel } from './escalaDisponibilidade.ts';
import type { AusenciaTrabalho, HorarioTrabalho } from '../../types';

export const ORIGEM_PAUSA = 'pausa';

/** Ausência que é uma pausa utilizável: origem certa e com recorte de horário.
 *  Uma pausa sem hora não existe — dia inteiro é ausência de gestor. */
export const ehPausa = (a: AusenciaTrabalho): boolean =>
    a.origem === ORIGEM_PAUSA && !a.dia_inteiro && !!a.hora_inicio && !!a.hora_fim;

/**
 * A pausa que está valendo neste instante, se houver.
 *
 * Uma pausa aberta é gravada com hora_fim = fim do expediente do dia (rede de
 * segurança do banco para quem esquece de voltar), então "aberta" aqui é
 * simplesmente "o agora cai dentro dela".
 */
export function pausaAberta(ausencias: AusenciaTrabalho[], agora: Date): AusenciaTrabalho | null {
    const { dataISO, minutos } = paraLocalBRT(agora);
    return ausencias.find(a =>
        ehPausa(a)
        && a.data === dataISO
        && horaParaMinutos(a.hora_inicio!) <= minutos
        && horaParaMinutos(a.hora_fim!) > minutos
    ) ?? null;
}

/**
 * Quanto tempo a pessoa passou pausada HOJE, em minutos — pausas já encerradas
 * mais o pedaço já decorrido da que estiver aberta.
 *
 * É o número que o gestor precisa ver: a pausa para o relógio do SLA, então sem
 * exibir o total pausado o indicador ficaria bonito sem ninguém saber o preço.
 */
export function minutosPausadosNoDia(ausencias: AusenciaTrabalho[], agora: Date): number {
    const { dataISO, minutos } = paraLocalBRT(agora);
    return ausencias
        .filter(a => ehPausa(a) && a.data === dataISO)
        .reduce((total, a) => {
            const inicio = horaParaMinutos(a.hora_inicio!);
            // A pausa aberta termina no futuro (fim do expediente): conta só até agora.
            const fim = Math.min(horaParaMinutos(a.hora_fim!), minutos);
            return total + Math.max(0, fim - inicio);
        }, 0);
}

export type EstadoSla = 'ativa' | 'pausada' | 'fora_expediente';

/**
 * Estado do botão do topo. A ordem importa: pausada é verificada ANTES de
 * estaDisponivel, senão a própria pausa (que é uma ausência) faria a pessoa
 * cair em "fora do expediente" e o botão de voltar sumiria.
 */
export function estadoSla(
    horarios: HorarioTrabalho[],
    ausencias: AusenciaTrabalho[],
    agora: Date
): EstadoSla {
    if (pausaAberta(ausencias, agora)) return 'pausada';
    return estaDisponivel(horarios, ausencias, agora) ? 'ativa' : 'fora_expediente';
}

/** '8min' · '1h 5min' — mesmo formato compacto do painel de leads parados. */
export function fmtDuracaoMin(min: number): string {
    const total = Math.max(0, Math.round(min));
    const horas = Math.floor(total / 60);
    const resto = total % 60;
    if (horas === 0) return `${resto}min`;
    return `${horas}h${resto > 0 ? ` ${resto}min` : ''}`;
}
